import { memo } from 'react';
import {
  IconButton,
  TextInput,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
} from '@/shared/ui';
import useRrPaginationContainer from './useRrPaginationContainer';

type RrPaginationContainerProps = {
  isDisabled?: boolean;
};

/**
 * Renders the page navigation control for the R-R heatmap beat list. Wires the page number input and the previous and next icon buttons to the handlers from useRrPaginationContainer, disabling each button at the ends of the range and whenever the container itself is disabled.
 *
 * @param isDisabled - True to disable the page input and both navigation buttons.
 * @returns The R-R beat pagination control with its page input and navigation buttons.
 */
const RrPaginationContainer = ({ isDisabled }: RrPaginationContainerProps) => {
  const {
    pageString,
    inputSize,
    isError,
    maxPageNumber,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  } = useRrPaginationContainer(isDisabled);

  return (
    <div className="rr-beat-pagination-container">
      <div className="rr-beat-pagination-content">
        <div className="beat-action-title">Navigation</div>
        <div className="rr-beat-pagination-control">
          <TextInput
            ariaLabel="Page number"
            suffix={`/ ${maxPageNumber}`}
            name="pageString"
            value={pageString}
            onChange={onChangePageString}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            isNumberFormat
            shouldChangeSize
            className="page-input"
            size={inputSize}
            isError={isError}
            disabled={isDisabled}
            decimalSeparator={false}
            id="rr-beat-page-input"
          />
          <IconButton
            isFilled
            disabled={isDisabledPrev || isDisabled}
            className="previous-page-btn"
            iconComponent={
              <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
            }
            onClick={onClickPrevious}
            id="rr-beat-previous-page-btn"
          />
          <IconButton
            isFilled
            disabled={isDisabledNext || isDisabled}
            className="next-page-btn"
            iconComponent={
              <img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />
            }
            onClick={onClickNext}
            id="rr-beat-next-page-btn"
          />
        </div>
      </div>
    </div>
  );
};

export default memo(RrPaginationContainer);
